import Vue from "vue";
import store from "../../store/index";
import { ItemStore, SpaceStore } from "../ELYXStore";
import ELYXUtils from "../ELYXUtils";
import SpaceUtilities from "../Spaces/SpaceUtilities";
import SubspaceUtilities from "../Subspaces/SubspaceUtilities";
const ItemStoreState = ItemStore.state;
const AuthStore = store.state.auth; // !!! AuthStore.user IS NOT AVAILABLE HERE, THAT IS WHY WE GET AUTHSTORE ONLY
import ItemAPI from "./ItemAPI";
import ItemConstants from "./ItemConstants";
const FIFTEEN_MIN_MS = 15 * 60 * 1000;

const ItemMethods = {
  /** ========== ITEM CREATOR ========== */
  /**
   * Initializes a new context, e.g. a subspace and adds
   * its items if any exist.
   * @param {*} items
   */
  initItemsContext(items = []) {
    if (items.length) {
      /** Add received items to central store */
      ItemStore.methods.addItemsToItems(items);

      /** Update skip value after adding items to the list. */
      ItemStore.methods.syncItemsSkipValue();
    }

    /** Since this is the first load, handle related things. */
    ItemStore.methods.handleItemsFirstLoad();

    /** Initializes queue for preloading items */
    ItemMethods.queueItemsForPreloading();
  },

  syncCreatorInput(value, itemType) {
    ItemStore.methods.syncCreatorInput(value, itemType);
  },

  async createOneItem() {
    const spaceID = SpaceUtilities.getSpaceID();
    if (!spaceID) return;

    /** Extract current creator type, e.g. 'TASK' or 'NOTE', etc. */
    const type = ItemStoreState.creator.currentSelectedType;

    /** Store the item in a variable to be able to clear the input for UX */
    const itemValue = Object.hasOwn(ItemStoreState.creator, type) ? ItemStoreState.creator[type] : "";

    /** Ensure input has value */
    if (!itemValue) return;

    /** Clear the input for UX */
    ItemStore.methods.clearCreatorInput(type);

    /** Construct placeholder (dummy) item to insert for UX */
    const placeholderItem = {
      _id: ELYXUtils.generateRandomID(), // construct random unique ID to identify placeholder item
      name: itemValue,
      type: ItemConstants.TYPES[type],
      loaded: Date.now(), // necessary to avoid attemps to pre-load item
    };

    /** Add placeholder to the list for fast UX appearance of item in list */
    ItemStore.methods.addOneItemToList(placeholderItem);

    await Vue.nextTick();

    /** Ensure that item added to list has its text visually at its beginning to handle long texts */
    document.getElementsByClassName("itemsListCard--title")[0].scrollLeft = 0;

    /** Prepare and make request to actually add item to the database */
    const itemData = { name: itemValue, spaceID, type };
    const properties = [];
    if (properties && properties.length) itemData.properties = properties;
    const subspaceID = SubspaceUtilities.getSubspaceID();
    if (subspaceID) itemData.subspaceID = subspaceID;

    const response = await ItemAPI.createOneItem(AuthStore.user._id, itemData);

    /** If request was successful, replace placeholder item with real created item */
    if (response && response.success && response.data) {
      /** Replace placeholder item with real created item */
      const newItem = response.data;
      ItemStore.methods.replaceOneItemInList(placeholderItem._id, newItem);

      /** Sync skip value now that we know the item has actually been created
       * in the database. We don't do it before (e.g. after adding placeholder)
       * because if something goes wrong, we'd remove the placeholder anyway. */
      ItemStore.methods.syncItemsSkipValue();
    } else {
      /** If request was not successful, remove placeholder item from list */
      ItemStore.methods.removeOneItemFromList(placeholderItem._id);

      /** Restore creator value */
      ItemStore.methods.syncCreatorInput(itemValue, type);
    }
  },

  /** ========== ITEMS LIST CARD CONTROLLER FUNCTIONS ========== */
  /**
   * Adds passed item to selected items, while ensuring no duplicates are added.
   * @param {Object} item
   */
  async selectOneItem(item) {
    /** Ensure not already selected */
    for (let selected of ItemStoreState.selectedItems) {
      if (selected._id === item._id) return;
    }

    /** When not using shift to select multiple, replace all selected */
    ItemStore.methods.replaceSelectedItems(item);

    /** Load the item's contents */
    await ItemMethods.loadItemContents(item);
  },

  isItemAlreadyRecentlyLoaded(item) {
    /** If item has already been loaded and done so less than 15m ago, don't attempt preload.  */
    const FIFTEEN_MIN_AGO = Date.now() - FIFTEEN_MIN_MS;
    if (item.loaded && item.loaded > FIFTEEN_MIN_AGO) return true;
    return false;
  },

  /** Used for loading and preloading item contents. */
  async loadItemContents(item) {
    /** Don't load item again if it's already been recently loaded */
    if (ItemMethods.isItemAlreadyRecentlyLoaded(item)) return;

    /** Make request to get item */
    const response = await ItemAPI.getOneItemByID(AuthStore.user._id, item._id);
    if (!response || !response.data || !response.data.item) {
      throw new Error("Failed to retrieve item.");
    }
    /** Replace old or unpopulated item with new item. */
    ItemStore.methods.replaceOneItemInList(item._id, response.data.item);

    /** If the item is selected, replace current selection with updated version */
    if (ItemStore.state.selectedItems.find((obj) => obj._id === item._id)) {
      ItemStore.methods.replaceSelectedItems(response.data.item);
    }
  },

  async preloadItem(item) {
    await ItemMethods.loadItemContents(item);
  },

  getItemsForPreloading() {
    /** Extract IDs of up to 15 items which don't have their up to date content loaded yet for preloading */
    let itemIDs = [];
    for (var i = 0; i < ItemStore.state.items.length; i++) {
      /** If reaches 15 items, end loop. */
      if (itemIDs.length === 15) break;
      const item = ItemStore.state.items[i];
      /** If not yet loaded OR has been loaded > 15m ago */
      if (!item.loaded || !ItemMethods.isItemAlreadyRecentlyLoaded(item)) {
        itemIDs.push(item._id);
      }
    }
    return itemIDs;
  },

  async preloadManyItems() {
    /** Get up to 15 items eligible for preloading */
    const itemIDs = ItemMethods.getItemsForPreloading();

    /** Do nothing if nothing to preload */
    if (!itemIDs.length) return;

    const response = await ItemAPI.preloadManyItemsByIDs(AuthStore.user._id, itemIDs);
    if (!response || !response.data || !response.data.items || !response.data.items.length) {
      throw new Error("Failed to retrieve item.");
    }

    /** Replace old or unpopulated items with populated items */
    ItemStore.methods.replaceManyItemsInList(response.data.items);

    /** Re-queue items for preloading in case more items could be preloaded */
    const moreItemIDs = ItemMethods.getItemsForPreloading();
    if (moreItemIDs.length) {
      ItemMethods.preloadMoreItemsWhenPossible();
    }
  },

  async preloadMoreItemsWhenPossible() {
    setTimeout(ItemMethods.queueItemsForPreloading, 5000);
  },

  /**
   * Returns an object containing a deep copy of passed in item
   * and the item's current index position in array of items.
   * @param {Object} item
   * @returns {
   * backupItem: Object,
   * index: Number
   * } Backup data for an item and its position
   */
  createBackupItem(item) {
    /** Store deep copy of the item and remove any references. */
    const backupItem = JSON.parse(JSON.stringify(item));

    /** Store item's current position */
    const itemIndex = ItemStoreState.items.findIndex((obj) => obj._id === item._id);

    /** Construct backup object for fail-safe. */
    const backupData = { item: backupItem, index: itemIndex };
    return backupData;
  },

  /**
   * Trashes or permanently deletes an item. If user is viewing trash
   * or their preference is to immediately delete items without trashing
   * them, then the item will be deleted. Otherwise, the item will be
   * moved to trash.
   *
   * Favors fast UX and a fail-safe as a backup.
   * @param {Object} item
   */
  async trashOrDeleteOneItem(item) {
    /** Construct backup object for fail-safe. */
    const backupData = ItemMethods.createBackupItem(item);

    /** Remove item from Data Store by Item ID. We remove it from the Data Store
     * before removing it from the database to provide rapid fast UX, while having
     * a fail-safe in place to undo the change in the Data Store in case something
     * goes wrong server-side. */
    ItemStore.methods.removeOneItemFromList(item._id);

    let response = null;
    let preferenceInContext = "trashBeforeDelete";
    preferenceInContext = "deleteImmediately";
    /** If user is viewing trash, permanently delete the item. */
    if (ItemStoreState.recentlyDeletedActive || preferenceInContext === "deleteImmediately") {
      /** Make request to delete item from the database by Item ID */
      response = await ItemAPI.deleteOneItem(AuthStore.user._id, item._id);
    } else {
      response = await ItemAPI.trashOneItem(AuthStore.user._id, item._id);
    }

    /** In case of error, undo change in Data Store using backupData object created before. */
    if (!response || !response.success) {
      ItemStore.methods.addOneItemBackToList(backupData);
    }

    /** Ensure number of items to skip in query is correct */
    ItemStore.methods.syncItemsSkipValue();
  },

  /**
   * Remove one item from trash.
   * @param {Object} item
   */
  async removeOneItemFromTrash(item) {
    /** Construct backup object for fail-safe. */
    const backupData = this.createBackupItem(item);

    /** Remove item from Data Store by Item ID. We remove it from the Data Store
     * before removing it from the database to provide rapid fast UX, while having
     * a fail-safe in place to undo the change in the Data Store in case something
     * goes wrong server-side. */
    ItemStore.methods.removeOneItemFromTrash(item);

    const response = await ItemAPI.removeOneItemFromTrash(AuthStore.user._id, item._id);

    /** In the case something went wrong server-side, undo changes in Data Store
     * using the backupData object created before. */
    if (!response || !response.success) {
      ItemStore.methods.addOneItemBackToTrash(backupData);
    }
  },

  async toggleComplete(item) {
    /** If not dealing with an item of type task, cancel complete */
    if (item.type !== ItemConstants.TYPES.TASK) return;

    if (!item.complete) {
      /** If item is not set to complete, then set it to complete
       * in the local data store for fast UX. */
      ItemStore.methods.completeOneItemByID(item._id);

      /** Then make request to set item as complete in the database. */
      const response = await ItemAPI.completeItemByID(AuthStore.user._id, item._id);

      /** If anything goes wrong with the request, revert item complete status. */
      if (!response || !response.success) {
        ItemStore.methods.uncompleteOneItemByID(item._id);
      }
    } else {
      /** If item is set to complete, then set it to uncomplete
       * in the local data store for fast UX. */
      ItemStore.methods.uncompleteOneItemByID(item._id);

      /** Then make request to set item as uncomplete in the database. */
      const response = await ItemAPI.uncompleteItemByID(AuthStore.user._id, item._id);

      /** If anything goes wrong with the request, revert item complete status. */
      if (!response || !response.success) {
        ItemStore.methods.completeOneItemByID(item._id);
      }
    }
  },

  async toggleSelectedItemComplete() {
    if (ItemStore.state.selectedItems.length) {
      const currentSelection = ItemStore.state.selectedItems[0];
      await ItemMethods.toggleComplete(currentSelection);
    }
  },

  setTitleAsEditing(item) {
    ItemStore.methods.setTitleAsEditing(item);
  },

  unsetTitleAsEditing() {
    ItemStore.methods.unsetTitleAsEditing();
  },

  syncItemEditorContent(newValue) {
    ItemStore.methods.syncItemEditorContent(newValue);
  },

  async updateItemContent(item) {
    /** Ensure editor has value and that the value is different
     * from the item's current content. */
    if (ItemStoreState.editorText && ItemStoreState.editorText !== item.name) {
      /** Prepare item backup in case of failure */
      const backupData = { backupContent: item.name, itemID: item._id };

      /** Create backup of editor state and turn off editing mode and
       * do it before updating item for smoother UX. */
      const editorBackup = { value: ItemStoreState.editorText };
      ItemStore.methods.unsetTitleAsEditing();

      /** Update actual item content value in local data store
       * with editor's most recent value. */
      const itemData = { itemID: item._id, newValue: editorBackup.value };
      ItemStore.methods.updateItemContent(itemData);

      /** Make request to update item content in the database. */
      const response = await ItemAPI.updateItemContentByID(AuthStore.user._id, item._id, editorBackup.value);
      if (!response || !response.success) {
        /** If anything goes wrong in the request, restore editor
         * and item states. */
        ItemStore.methods.restoreEditorText(editorBackup);
        ItemStore.methods.undoUpdateItemContent(backupData);
      }
    }
  },

  /**
   *
   * @param {*} $state InfiniteLoading library state object
   * @returns
   */
  async loadMoreItems($state) {
    /** Wait for all UI to load before loading more items. */
    await Vue.nextTick();

    if (ItemStoreState.allDataForPropertyLoaded) {
      $state.loaded();
      return;
    }
    /* const currentlySelectedPropertyID = this.selectedProperty ? this.selectedProperty._id : "";
    const propertyIDs = currentlySelectedPropertyID ? [currentlySelectedPropertyID] : []; */
    const propertyIDs = [];
    const subspaceID = SubspaceUtilities.getSubspaceID();
    const spaceID = SpaceUtilities.getSpaceID();
    let items = [];

    /** Set items as loading */
    ItemStore.methods.setCurrentlyLoadingItems(true);

    if (subspaceID) {
      items = await ItemAPI.getItemsByContextID(AuthStore.user._id, spaceID, subspaceID, ItemStore.state.options);
    } else {
      /** Initialize items query filters */
      const filters = {};
      if (propertyIDs && propertyIDs.length) filters.propertyIDs = propertyIDs;
      if (ItemStoreState.itemType) filters.type = ItemStoreState.itemType;
      items = await ItemAPI.getItems(AuthStore.user._id, spaceID, ItemStoreState.options, filters);
    }
    if (items && items.length) {
      ItemMethods.addRetrievedItemsToListAndUpdateSkip(items);
      $state.loaded();
    } else {
      /* ========== All items loaded for this property. ==========
       * NB! Don't use InfiniteLoading's $state.complete() because it
       * then won't load more in case you switch tags and need to load
       * more there. */
      ItemStore.methods.setAllDataForPropertyLoaded(true);
      $state.loaded();
    }

    /** Set items as no longer loading */
    ItemStore.methods.setCurrentlyLoadingItems(false);

    /** Set a debouncer to preload items once no new items are being loaded */
    await ItemMethods.queueItemsForPreloading();
  },

  /** Updates items preloading queue */
  async queueItemsForPreloading() {
    const earliestAllowedUpdateMS = Date.now();
    const thresholdMS = 2 * 1000;
    await ItemStore.state.preloadDebouncer(earliestAllowedUpdateMS, thresholdMS, async () => await ItemMethods.preloadManyItems());
  },

  addRetrievedItemsToListAndUpdateSkip(items) {
    ItemStore.methods.addItemsToItems(items);
    ItemStore.methods.syncItemsSkipValue();
  },

  // async createOne(item) {
  //   if (!item) return;

  //   /* Store tags before clearing them */
  //   /* const currentTags = this.tags; */
  //   const itemObject = { content: item, type: this.itemType, properties: [] };
  //   this.clearTags();
  //   this.clearTitle();
  //   const response = await ItemAPI.createOneItem(this.user._id, itemObject, this.itemType);
  //   if (!response || !response.success || !response.item) throw new Error("Failed to create item.");
  //   const returnedItem = response.item;
  //   this.items.unshift(returnedItem);
  //   if (returnedItem.properties && returnedItem.properties.length) {
  //     this.addPropertiesNotYetInProperties(returnedItem.properties);
  //   }
  // },

  goToEditor() {
    this.ckEditor.editing.view.focus();
  },

  setItemType(type) {
    ItemStore.methods.setItemType(type);
  },
  /* ========== ITEMS ========== */

  /* async getInitialData() {
    const response = await ItemAPI.getInitialItemsData(this.user._id, this.itemType);
    if (!response || !response.success) throw new Error("Failed to load initial data.");
    if (response.items && response.items.length) {
      this.addRetrievedItemsToListAndUpdateSkip(response.items);
    }
    if (response.properties && response.properties.length) {
      this.properties = response.properties;
    }
  }, */

  /**
   * Set new selected property and load new content accordingly.
   *
   * @param {Object} property — New selected property user wants to set
   */
  async setAndLoadPropertyContent(property, settingRecentlyDeleted = false) {
    /* If not currently setting recently deleted view, then reset it if it's on */
    if (!settingRecentlyDeleted) this.resetRecentlyDeletedView();

    /* If property is already selected, don't do anything. */
    if (property && this.selectedProperty && this.selectedProperty._id === property._id) return;

    /* Set tag switching to true to prevent InfiniteLoader from attempting to load
     * items while switching tags. */
    this.switchingTag = true;

    /* Since new tag set is queried, reset all states */
    this.allDataForPropertyLoaded = false;
    this.items = [];
    this.options.skip = 0;

    /* If a tag is currently selected, remove it from default tags */
    if (this.selectedProperty) {
      const previousPropertyIndex = this.tags.findIndex((obj) => obj === this.selectedProperty.name);
      this.tags.splice(previousPropertyIndex, 1);
    }

    /* Set new selected property as selected property */
    const propertyIDs = [];
    if (property) {
      this.selectedProperty = property;

      /* Add new property into tags */
      const newPropertyName = this.selectedProperty.name;
      if (!this.tags.includes(newPropertyName)) {
        this.addTag(newPropertyName);
        this.overrideTagsShow = true;
      }

      propertyIDs.push(property._id);
    }

    const items = await ItemAPI.getItems(this.user._id, this.itemType, propertyIDs, this.options);
    if (items && items.length) {
      this.addRetrievedItemsToListAndUpdateSkip(items);
    }

    /* Conclude and end property switching process */
    this.switchingTag = false;
  },

  /* ====================================== */
  /* ========== HELPER FUNCTIONS ========== */
  /* ====================================== */

  /* ========== ITEMS ========== */
  replaceInList(replaceData) {
    const { oldID, newItem } = replaceData;
    const itemIndex = this.items.findIndex((item) => item._id === oldID);
    this.items.splice(itemIndex, 1, newItem);
  },
  clearTitle() {
    this.itemTitle = "";
  },

  removeTag(tagIndex, itemID) {
    ItemStore.methods.removeTag(itemID, tagIndex);

    /** Make request to remove tag from database */

    /** Upon failed request, add tag back to item */
  },
  removeLastTag(itemID) {
    ItemStore.methods.removeLastTag(itemID);
  },

  /** ========== ITEM TAGGER MANAGER ========== */

  /**
   * @param {Object} item Item to toggle tagger for
   * @param {String} componentThatCalledTagger Where the tagger is being opened from (e.g. "ItemViewerCard")
   */
  toggleItemTagger(item, componentThatCalledTagger) {
    /** If editTracker is not yet enabled for the item, enable it as it
     * is required for the tagger to work, since all tagger changes for
     * any item's tagger component are tracked inside the editTracker. */
    if (!Object.hasOwn(ItemStoreState.editTracker, item._id)) {
      ItemStore.methods.initItemTracking(item);
    }

    ItemStore.methods.toggleItemTagger(item._id, componentThatCalledTagger);
  },

  /**
   * @param {String} itemID ID of item to close tagger for
   */
  closeItemTagger(itemID) {
    ItemStore.methods.closeItemTagger(itemID);
  },

  /**
   * Syncs a specific item's tag input value with the item's edit tracker tag value
   * @param {String} tagName Tag input value
   * @param {String} itemID ID of item to sync tag input value with
   */
  syncEditTrackerTagName(tagName, itemID) {
    ItemStore.methods.syncEditTrackerTagName(itemID, tagName);
  },

  /**
   * Adds a tag to an item. Optimized for fast UI/UX using a placeholder tag
   * and replacing it with the actual tag once the request is successful.
   */
  async addTagToItem(itemID) {
    /** Ensure editTracker exists for item. */
    if (!Object.hasOwn(ItemStoreState.editTracker, itemID)) return;

    /** Get tag name value from editTracker. */
    const tagName = ItemStoreState.editTracker[itemID].itemTagger.tagName;

    /** Reject if tagger input has no value. */
    if (!tagName) return;

    /** Check whether new tag name value matches an existing tag's name. */
    const existingTag = SpaceStore.state.properties.find((tag) => tag.name === tag);

    /** Extract SpaceID */
    const spaceID = SpaceUtilities.getSpaceID(itemID);

    /** Ensure spaceID is provided so that tag would have proper context. */
    if (!spaceID) return;

    /** Construct placeholder tag */
    const placeholderTag = { _id: ELYXUtils.generateRandomID(), name: tagName };

    /** Add placeholder tag to item */
    ItemStore.methods.addTagToItem(itemID, existingTag ? existingTag : placeholderTag);

    /** Clear Tagger input */
    ItemStore.methods.clearEditTrackerTagName(itemID);

    /** Meta data setup to allow flexibly adding more contextual information in the future to a tag
     * such as hierarchical position, color, icon, etc. */
    const tagMetadata = existingTag && existingTag.metadata ? existingTag.metadata : { color: "#000000" };

    /** Make request to a new property to the item */
    const prop = { name: tagName, type: "TAG", contextID: spaceID, contextType: "SPACE", metadata: tagMetadata };
    const response = await ItemAPI.addOnePropertyToItem(AuthStore.user._id, itemID, prop);

    /** Upon successful request, replace dummy tag with actual tag */
    /** If request was successful, replace placeholder item with real created item */
    if (response && response.success && response.data) {
      const { property, lastModifiedMS } = response.data;
      ItemStore.methods.replaceOneItemTag(itemID, placeholderTag._id, property);
      ItemStore.methods.updateLastModifiedDate(itemID, lastModifiedMS);
    } else {
      /** If request was not successful, remove placeholder tag from item */
      ItemStore.methods.removeOneTagFromItem(itemID, placeholderTag._id);

      /** Restore text search via backup*/
      /* ItemStore.methods.syncCreatorInput({ value: tagName, type: "tag" }); */
    }
  },

  clearTags() {
    if (this.selectedProperty && this.selectedProperty.name) {
      this.tags = [this.selectedProperty.name];
    } else {
      this.tags = [];
    }
  },
  cancelOverrideTagsShow() {
    this.overrideTagsShow = false;
  },

  addPropertiesNotYetInProperties(properties) {
    for (var i = 0; i < properties.length; i++) {
      const property = properties[i];
      const propertyExists = this.properties.find((obj) => obj._id === property._id);
      if (!propertyExists) this.properties.push(property);
    }
  },

  async setRecentlyDeletedView() {
    /* If already recently deleted view, do nothing */
    if (this.recentlyDeletedActive) return;

    /* Set recently deleted view to active */
    this.recentlyDeletedActive = true;

    /* Reset any currently active properties */
    this.selectedProperty = {};

    /* Query items that are recently deleted */
    await this.setAndLoadPropertyContent(null, true);
  },

  resetRecentlyDeletedView() {
    this.recentlyDeletedActive = false;
  },

  /** ========== Item Viewer Card ========== */
  async updateField(newValue, item, field) {
    /** Store itemID for re-use */
    const itemID = item._id;

    /** Sync item value in original array */
    ItemStore.methods.syncOriginalItemValue(itemID, field, newValue);

    /** Update item passed in with new value */
    item[field] = newValue;

    /** Initialize item tracking if not yet tracking. */
    ItemStore.methods.initItemTracking(item);

    /** Set flag that a particular field has been changed. */
    ItemStore.methods.updateRecentlyChangedField(itemID, field);

    /** Attempt perform update for item 2 seconds after user finished typing. */
    const earliestAllowedUpdateMS = Date.now();
    const threshold = 2 * 1000;
    await ItemStoreState.editTracker[itemID].debounce(earliestAllowedUpdateMS, threshold, async () => await ItemMethods.prepareItemForUpdate(itemID));
  },

  /**
   * This uses itemID, because we can use the blur event in the editor
   * to make a change and at that time, the item available there does
   * not yet have the latest updated fields.
   *
   * @param {*} itemID
   * @returns
   */
  async prepareItemForUpdate(itemID) {
    /** If no item with such ID is being tracked, cancel update. */
    if (!Object.hasOwn(ItemStoreState.editTracker, itemID)) {
      return;
    }

    /** Get updated item copy */
    const item = ItemStoreState.editTracker[itemID].item;

    /** Construct update object based on recently changed fields. */
    let updateObject = ItemMethods.constructUpdateObject(item);

    /** Clear recently changed fields. */
    ItemStore.methods.clearRecentlyChangedFields(itemID);

    /** If no fields have been updated, don't do anything. */
    if (!Object.keys(updateObject).length) {
      return;
    }

    /** If the item is still saving for a previous update, queue the update to
     * avoid race conditions. */
    if (ItemStoreState.editTracker[item._id].savingInProgress) {
      ItemStore.methods.queueItemUpdate(item._id, updateObject);
      return;
    }

    /** Perform the actual update. */
    await ItemMethods.performItemUpdate(item._id, updateObject);
  },

  async performItemUpdate(itemID, updateObject) {
    /** If the item has a previous save in a failed state, make an update for
     * all fields to minimize chances of data loss due to previous update failing. */
    if (ItemStoreState.editTracker[itemID].saveFailed) {
      updateObject = ItemMethods.constructUpdateObjectWithAllFields(itemID);
    }

    /** Set item as saving before sending request. */
    ItemStore.methods.setItemAsSaving(itemID);

    /** Make request to update item. */
    const response = await ItemAPI.updateItemByID(AuthStore.user._id, itemID, updateObject);

    if (!response || !response.success || !response.data) {
      /** If saving failed, set item saving status as failed.  */
      ItemStore.methods.setItemSavingFailed(itemID);
    } else {
      /** Check if saving for the item had been in a failed state. */
      if (ItemStoreState.editTracker[itemID].savingFailed) {
        /** If it was in a failed state, then unset it now as the most
         * recent save was successful. */
        ItemStore.methods.unsetItemSavingFailed(itemID);
      }

      /** Update lastModifiedMS for original item */
      const lastModifiedMS = response.data;
      ItemStore.methods.updateLastModifiedDate(itemID, lastModifiedMS);
    }

    /** Unset item as saving as request is finished. */
    ItemStore.methods.unsetItemAsSaving(itemID);

    /** Take care of any queued updates for the item. */
    await ItemMethods.handleItemQueuedUpdates(itemID);

    /** Mark last edit to remove from editTracking */
  },

  /**
   * Constructs an update object based on recently changed fields.
   * @param {Object} item
   * @returns {Object}
   */
  constructUpdateObject(item) {
    const updateObject = {};
    if (Object.hasOwn(ItemStoreState.editTracker, item._id)) {
      const fields = ItemStoreState.editTracker[item._id].recentlyChangedFields;
      for (let field of fields) {
        updateObject[field] = item[field];
      }
    }
    return updateObject;
  },

  /**
   * Constructs an update object with all fields of the item. Usually used
   * when a save has failed and a safe update with all the mos
   * @param {Object} item
   * @returns {Object} Object with all fields of an item, except the _id field.
   */
  constructUpdateObjectWithAllFields(itemID) {
    const updateObject = {};
    if (Object.hasOwn(ItemStoreState.editTracker, itemID)) {
      const item = ItemStoreState.editTracker[itemID].item;
      for (let field of item) {
        if (field !== "_id") {
          updateObject[field] = item[field];
        }
      }
    }
    return updateObject;
  },

  /**
   * Handles a queued save for a particular item if any exist.
   * @param {String} itemID
   */
  async handleItemQueuedUpdates(itemID) {
    if (Object.hasOwn(ItemStoreState.editTracker, itemID)) {
      if (ItemStoreState.editTracker[itemID].savingQueue.length) {
        const updateObject = ItemStoreState.editTracker[itemID].savingQueue[0];
        ItemStoreState.editTracker[itemID].savingQueue.shift();
        await ItemMethods.performItemUpdate(itemID, updateObject);
      }
    }
  },

  async selectNextItemInList() {
    if (ItemStoreState.selectedItems.length) {
      const currentSelectionID = ItemStoreState.selectedItems[0]._id;
      const indexInItems = ItemStoreState.items.findIndex((item) => item._id === currentSelectionID);

      /** Check if selection is last item in list */
      if (indexInItems === ItemStoreState.items.length - 1) {
        /** If all items are loaded   */
        if (ItemStoreState.allDataForPropertyLoaded) {
          /** All items are loaded so not possible to go to another item. */
          return;
        } else {
          /** Trigger scroll event to load more items. */
          const el = document.querySelector(":root");
          el.dispatchEvent(new CustomEvent("scroll"));
        }
      }
      const nextItemIndex = indexInItems + 1;
      const nextItem = ItemStoreState.items[nextItemIndex];

      /** No items currently at X position  — may need to load more */
      if (!nextItem) return;

      /** Select next item in list */
      ItemMethods.selectOneItem(nextItem);
    }
  },

  async selectPrevItemInList() {
    if (ItemStoreState.selectedItems.length) {
      /** Get the ID of the currently selected item. */
      const currentSelectionID = ItemStoreState.selectedItems[0]._id;

      /** Get the index of the selected item in the original array. */
      const indexInItems = ItemStoreState.items.findIndex((item) => item._id === currentSelectionID);

      /** Check if selection is first in list */
      if (indexInItems === 0) return;

      const prevItemIndex = indexInItems - 1;
      const prevItem = ItemStoreState.items[prevItemIndex];

      /** Select next item in list */
      ItemMethods.selectOneItem(prevItem);
    }
  },
};

/* const ItemCreatorProps = {
  type: String,
  config: Object,
  tags: Array,
  value: String,
  overrideTagsShow: Boolean,
}; */

export default ItemMethods;
